import { useEffect, useState } from "react";
import EventEHRLazyDropdown from "../lazy-loading-search/EventEHRLazyLoadingSearch";
import { getLazyLoadingApiResponse } from "../../../utils/constantFunctions";
import { getFacilityId, getLoginUserData } from "../../../utils/getToken";
import AddUnitNumberDialog from "./AddUnitNumberDialog";

// The full basecamp record surfaced to the parent on select, so it can auto-fill
// the unit leader name / email / contact fields.
export interface UnitNumberRecord {
  id: string;
  unitNumber: string;
  leaderName?: string;
  email?: string;
  leaderContact?: string;
}

interface UnitOption {
  label: string;
  value: string;
  record: UnitNumberRecord;
}

interface UnitNumberSelectProps {
  // Preselect (edit forms): { label: unitNumber, value: basecamp id }.
  defaultValue?: { label: string; value: string } | null;
  onChange: (record: UnitNumberRecord | null) => void;
  isDisabled?: boolean;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  // Show a "+ Add New Unit Number" footer that opens the create-basecamp modal.
  allowCreate?: boolean;
}

const PAGE_LIMIT = 25;

const mapBaseCamp = (list: any[]): UnitOption[] =>
  (list || [])
    .filter((b: any) => b?.id)
    .map((b: any) => ({
      label: b?.unitNumber || "",
      value: b?.id,
      record: {
        id: b?.id,
        unitNumber: b?.unitNumber,
        leaderName: b?.leaderName,
        email: b?.email,
        leaderContact: b?.leaderContact,
      },
    }));

// "Unit Number" dropdown with infinite scroll over the paginated /basecamp API.
// Pagination loads more on scroll-to-bottom; typing filters the loaded options
// client-side. Selecting a unit hands the full basecamp record to the parent.
const UnitNumberSelect = ({
  defaultValue = null,
  onChange,
  isDisabled = false,
  label,
  placeholder = "Select Unit Number",
  error = false,
  helperText = "",
  allowCreate = false,
}: UnitNumberSelectProps) => {
  const loginUserData: any = getLoginUserData();
  const facilityId =
    loginUserData?.customAttributes?.facilityId || getFacilityId();

  const [pageCount, setPageCount] = useState(1);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const [dropdownOptions, setDropdownOptions] = useState<UnitOption[]>(
    defaultValue?.value
      ? [
          {
            label: defaultValue.label,
            value: defaultValue.value,
            record: { id: defaultValue.value, unitNumber: defaultValue.label },
          },
        ]
      : [],
  );
  const [selectedValue, setSelectedValue] = useState<any>(defaultValue);
  const [resetFlag, setResetFlag] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [prefilledUnit, setPrefilledUnit] = useState("");

  // A new unit number was created: select it (fills campId + leader fields on
  // the parent) and re-sync the list with the server.
  const handleCreated = (record: UnitNumberRecord) => {
    setOpenAddDialog(false);
    if (record?.id) {
      const option: UnitOption = {
        label: record.unitNumber || "",
        value: record.id,
        record,
      };
      setDropdownOptions((prev) =>
        prev.some((p) => p.value === option.value) ? prev : [option, ...prev],
      );
      setSelectedValue(option);
      onChange(record);
    }
    setResetFlag(true);
  };

  useEffect(() => {
    if (!resetFlag) {
      getDropdownList(pageCount);
    }
  }, [pageCount]);

  useEffect(() => {
    if (resetFlag) {
      resetList();
    }
  }, [resetFlag]);

  const resetList = () => {
    setPageCount(1);
    getDropdownList(1, true);
  };

  const getDropdownList = async (page: number, reset = false) => {
    try {
      // Don't page past the last page (scroll keeps bumping pageCount).
      if (!reset && page > 1 && page > totalPages) return;
      const response = await getLazyLoadingApiResponse(
        "basecamp",
        page,
        PAGE_LIMIT,
        facilityId,
      );
      const list = mapBaseCamp(response?.data?.basecamp || []);
      setTotalPages(response?.data?.totalPages || 1);
      if (reset) {
        setDropdownOptions(list);
        setResetFlag(false);
      } else {
        setDropdownOptions((prev) => {
          const fresh = list.filter(
            (l) => !prev.some((p) => p.value === l.value),
          );
          return prev.concat(fresh);
        });
      }
    } catch (error) {
      console.log("error:", error);
      if (reset) setResetFlag(false);
    }
  };

  return (
    <>
      <EventEHRLazyDropdown
        isDisabled={isDisabled}
        label={label}
        placeholder={placeholder}
        error={error}
        helperText={helperText}
        noOptionText="No unit numbers found"
        dropdownOptions={dropdownOptions}
        dropdownSearch={dropdownSearch}
        selectedValue={selectedValue}
        setDropdownSearch={(data: string) => setDropdownSearch(data)}
        setPageCount={(data: any) => setPageCount(data)}
        setResetFlag={(data: boolean) => setResetFlag(data)}
        setSelectedValue={(data: any) => {
          setSelectedValue(data);
          onChange(data?.record || null);
        }}
        showCreateOption={allowCreate}
        createOptionLabel="+ Add New Unit Number"
        onCreateClick={(search: string) => {
          setPrefilledUnit(search || "");
          setOpenAddDialog(true);
        }}
      />
      {openAddDialog && (
        <AddUnitNumberDialog
          isOpen={openAddDialog}
          defaultUnitNumber={prefilledUnit}
          handleClose={() => setOpenAddDialog(false)}
          onCreated={handleCreated}
        />
      )}
    </>
  );
};

export default UnitNumberSelect;
